import React, { useEffect, useState } from "react";
import { Play, X, ChevronLeft, ChevronRight } from "lucide-react";
import { api, assetUrl } from "../lib/api";
import Reveal from "../components/site/Reveal";
import { useReveal } from "../lib/useReveal";

const Videos = () => {
  const [videos, setVideos] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [active, setActive] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/videos").then((r) => setVideos(r.data || [])).catch(() => {}).finally(() => setLoaded(true));
    api.get("/photos").then((r) => setPhotos(r.data || [])).catch(() => {});
  }, []);
  useReveal([videos.length, photos.length]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && (setActive(null), setLightbox(null));
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="pt-[64px] bg-[#12201A] min-h-screen" data-testid="videos-page">
      <div className="max-w-[1500px] mx-auto px-5 md:px-8 py-14">
        <p className="eyebrow text-[#C9B79C] mb-3">Watch</p>
        <Reveal as="h1" className="font-display font-light text-4xl md:text-5xl text-[#F3EEE3]">Videos</Reveal>
        <p className="text-[#F3EEE3]/60 mt-3 max-w-2xl">
          Fly over Deer Park Estates and see the ponds, parks and homes come to life. New videos are added regularly from our YouTube channel.
        </p>

        {videos.length === 0 ? (
          <div className="mt-12 text-[#F3EEE3]/50" data-testid="videos-empty">
            {loaded ? (
              <>Videos are temporarily unavailable. <a href="https://www.youtube.com/channel/UCCgHDqBiXpELdTfhHZ37Sxw/videos" target="_blank" rel="noopener noreferrer" className="text-[#E8B86D] hover:underline">Watch on YouTube</a>.</>
            ) : "Videos are loading…"}
          </div>
        ) : (
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="videos-grid">
            {videos.map((v) => (
              <button
                key={v.id}
                type="button"
                onClick={() => setActive(v)}
                data-testid={`video-card-${v.id}`}
                className="group text-left reveal"
              >
                <div className="relative aspect-video rounded-2xl overflow-hidden border border-[#F3EEE3]/12 bg-[#0C1510]">
                  <img src={v.thumbnail} alt={v.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-[#0C1510]/30 group-hover:bg-[#0C1510]/15 transition-colors" />
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="w-14 h-14 rounded-full bg-[#F3EEE3]/95 text-[#12201A] flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                      <Play size={22} className="ml-0.5 fill-current" />
                    </span>
                  </span>
                </div>
                <p className="mt-3 text-[#F3EEE3] font-medium">{v.title}</p>
              </button>
            ))}
          </div>
        )}

        {/* Photos */}
        <div className="mt-20" data-testid="photo-gallery">
          <p className="eyebrow text-[#C9B79C] mb-3">Gallery</p>
          <Reveal as="h2" className="font-display font-light text-3xl md:text-4xl text-[#F3EEE3]">Around the community</Reveal>
          <p className="text-[#F3EEE3]/60 mt-3 max-w-2xl">Ponds, parks, trails and homes across Deer Park Estates.</p>
          {photos.length === 0 ? (
            <p className="mt-8 text-[#F3EEE3]/50">Photos are loading…</p>
          ) : (
            <div className="mt-8 columns-1 sm:columns-2 lg:columns-3 gap-4 [column-fill:_balance]">
              {photos.map((p, i) => (
                <button
                  key={p.id || p.image}
                  type="button"
                  onClick={() => setLightbox(i)}
                  data-testid={`photo-${i}`}
                  className="group mb-4 block w-full overflow-hidden rounded-2xl border border-[#F3EEE3]/12 bg-[#0C1510] break-inside-avoid"
                >
                  <img src={assetUrl(p.image)} alt={p.alt} loading="lazy" className="w-full h-auto object-cover group-hover:scale-[1.03] transition-transform duration-700" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {active && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="video-modal">
          <div className="absolute inset-0 bg-[#0C1510]/90 backdrop-blur-sm" onClick={() => setActive(null)} />
          <div className="relative w-full max-w-4xl">
            <button
              onClick={() => setActive(null)}
              data-testid="video-modal-close"
              className="absolute -top-11 right-0 text-[#F3EEE3]/70 hover:text-[#F3EEE3] inline-flex items-center gap-1.5 text-sm"
            >
              Close <X size={18} />
            </button>
            <div className="aspect-video rounded-2xl overflow-hidden border border-[#F3EEE3]/15 bg-black">
              <iframe
                className="w-full h-full"
                src={`https://www.youtube-nocookie.com/embed/${active.id}?autoplay=1&rel=0`}
                title={active.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <p className="mt-3 text-[#F3EEE3] font-medium">{active.title}</p>
          </div>
        </div>
      )}
      {lightbox !== null && photos[lightbox] && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="photo-lightbox">
          <div className="absolute inset-0 bg-[#0C1510]/92 backdrop-blur-sm" onClick={() => setLightbox(null)} />
          <button onClick={() => setLightbox(null)} data-testid="photo-lightbox-close" className="absolute top-5 right-5 z-10 text-[#F3EEE3]/70 hover:text-[#F3EEE3] inline-flex items-center gap-1.5 text-sm">
            Close <X size={18} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i - 1 + photos.length) % photos.length); }} data-testid="photo-prev" className="absolute left-3 md:left-6 z-10 w-11 h-11 rounded-full bg-[#F3EEE3]/10 hover:bg-[#F3EEE3]/20 text-[#F3EEE3] flex items-center justify-center">
            <ChevronLeft size={22} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i + 1) % photos.length); }} data-testid="photo-next" className="absolute right-3 md:right-6 z-10 w-11 h-11 rounded-full bg-[#F3EEE3]/10 hover:bg-[#F3EEE3]/20 text-[#F3EEE3] flex items-center justify-center">
            <ChevronRight size={22} />
          </button>
          <figure className="relative max-w-5xl max-h-[85vh]">
            <img src={assetUrl(photos[lightbox].image)} alt={photos[lightbox].alt} className="max-w-full max-h-[85vh] object-contain rounded-xl" />
          </figure>
        </div>
      )}
    </div>
  );
};

export default Videos;
